import { Link, useLocation } from "react-router-dom";
import { Facebook, Instagram, Youtube, Music2, Phone, Mail, MapPin, Globe, ArrowRight } from "lucide-react";
import { BLUEPRINT_CHECKOUT_URL, withUTM } from "@/config/blueprint";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/blueprint", label: "The Program" },
  { to: "/hands-on-training", label: "Hands-On Training" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

// Program links — the last item is the primary conversion CTA. Kept as
// `Apply Now → /apply` on every page except /blueprint, where the footer
// swaps to a $497 Blueprint checkout CTA that mirrors the header.
const PROGRAM_LINKS = [
  { to: "/blueprint", label: "Curriculum" },
  { to: "/hands-on-training", label: "Training Days" },
  { to: "/included", label: "Who It's For" },
  { to: "/apply", label: "Apply Now" },
];

export default function Footer() {
  const { pathname } = useLocation();
  const isBlueprint = pathname === "/blueprint";
  const onFooterBlueprintClick = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("bcu:open-blueprint-modal", { detail: { placement: "footer" } }));
  };
  return (
    <footer data-testid="site-footer" className="bg-black border-t border-[rgba(217,164,65,0.22)]">
      <div className="bcu-container-wide py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <img src="/assets/bcu_logo_official.png" alt="Bounce City University" className="h-24 w-auto" />
            <div className="mt-6 text-[13px] tracking-[0.20em] uppercase text-[#F7931E] font-semibold">Bounce City University</div>
            <p className="mt-4 text-[15px] text-[#B5B5B5] max-w-sm leading-relaxed">The #1 mentorship for aspiring inflatable rental business owners. Real systems. Real training. Real results.</p>
            <div className="mt-6 flex items-center gap-3">
              {[Facebook, Instagram, Youtube, Music2].map((Icon, i) => (
                <a key={i} href="#" data-testid={`footer-social-${i}`} aria-label="social" className="inline-flex h-10 w-10 items-center justify-center border border-[rgba(255,255,255,0.16)] rounded text-[#B5B5B5] hover:text-[#F7931E] hover:border-[#F7931E] transition-colors">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-[12px] tracking-[0.24em] uppercase text-[#F7931E] font-semibold">Quick Links</div>
            <ul className="mt-5 space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}><Link to={l.to} className="text-[15px] text-[#B5B5B5] hover:text-white transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[12px] tracking-[0.24em] uppercase text-[#F7931E] font-semibold">The Program</div>
            <ul className="mt-5 space-y-3">
              {PROGRAM_LINKS.map((l, i) => {
                // On /blueprint, replace the Apply Now link with a Blueprint
                // checkout CTA wired to BLUEPRINT_CHECKOUT_URL (with UTMs).
                if (isBlueprint && l.label === "Apply Now") {
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={onFooterBlueprintClick}
                        data-testid="footer-blueprint-cta"
                        className="inline-flex items-center gap-2 text-[15px] text-[#F7931E] hover:text-white transition-colors font-semibold"
                      >
                        Get the Blueprint — $497 <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={i}><Link to={l.to} className="text-[15px] text-[#B5B5B5] hover:text-white transition-colors">{l.label}</Link></li>
                );
              })}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[12px] tracking-[0.24em] uppercase text-[#F7931E] font-semibold">Contact</div>
            <ul className="mt-5 space-y-3.5">
              <li className="flex items-start gap-3 text-[15px] text-[#B5B5B5]"><Phone className="h-4 w-4 text-[#F7931E] mt-0.5 flex-shrink-0" /><a href="tel:+13214936536" className="hover:text-white">321-493-6536</a></li>
              <li className="flex items-start gap-3 text-[15px] text-[#B5B5B5]"><Mail className="h-4 w-4 text-[#F7931E] mt-0.5 flex-shrink-0" /><a href="mailto:info@bouncecity321.com" className="hover:text-white">info@bouncecity321.com</a></li>
              <li className="flex items-start gap-3 text-[15px] text-[#B5B5B5]"><MapPin className="h-4 w-4 text-[#F7931E] mt-0.5 flex-shrink-0" /><span>321 Space Coast, Florida</span></li>
              <li className="flex items-start gap-3 text-[15px] text-[#B5B5B5]"><Globe className="h-4 w-4 text-[#F7931E] mt-0.5 flex-shrink-0" /><a href="https://www.bouncecity321.com" target="_blank" rel="noopener noreferrer" className="hover:text-white">www.bouncecity321.com</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-[rgba(255,255,255,0.08)] flex flex-wrap items-center justify-between gap-4 text-xs text-[#858585]">
          <div>© 2026 Bounce City University. All Rights Reserved.</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link>
            <Link to="/refund-policy" className="hover:text-white">Refund Policy</Link>
            <Link to="/earnings-disclaimer" className="hover:text-white">Earnings Disclaimer</Link>
            <Link to="/funding-disclaimer" className="hover:text-white">Funding Disclaimer</Link>
          </div>
          <div>Built on Experience. Designed for <span className="text-[#D9A441]">Legacy</span>.</div>
        </div>

        {/* Operating-brand relationship line — required for A2P 10DLC carrier
            trust reviews. Address deliberately kept out of the footer; the
            full registered address lives in Privacy Policy + Terms & Conditions
            and is submitted separately to TCR / GHL for brand registration. */}
        <p data-testid="footer-operating-line" className="mt-6 text-xs text-[#858585]">Bounce City University is operated by Bounce City 321 LLC.</p>

        <p className="mt-8 max-w-4xl text-xs text-[#858585] leading-relaxed">Bounce City University provides business education, mentorship, systems, templates and support. Individual results vary. Funding, income, profit and business success are not guaranteed. Organization names referenced on this site describe prior service relationships only and do not imply endorsement.</p>
      </div>
    </footer>
  );
}
